import React, { Fragment, useContext, useState } from 'react';
import { Link as ReachLink, Redirect } from 'react-router-dom';
import { Box, Heading, Text, Link } from '@chakra-ui/core';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

import { AuthContext } from '../../context/authContext';

import AuthHeader from '../../container/Layout/AuthHeader';
import FormInput from '../../components/form/input';
import FormButton from '../../components/form/button';
import FormCheckBox from '../../components/form/checkbox';
import AuthFooter from '../../container/Layout/AuthFooter';

const SignInSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email')
    .required('Email is Required'),
  password: Yup.string().required('Password is Required')
});

const Login = ({ history }) => {
  document.title = 'CrowdFarmer | Login';

  const { login, auth, isAuthenticated } = useContext(AuthContext);

  const [rememberMe, setRememberMe] = useState(false);

  if (isAuthenticated()) return <Redirect to={'/home'} />;

  return (
    <Fragment>
      <AuthHeader />
      <Box className="sign-in">
        <Box
          pt={16}
          px={{ base: 2, md: 0 }}
          mx="auto"
          pos={{ md: 'relative' }}
          className="container"
        >
          <Box w="100%" textAlign="center" mt={10}>
            <Heading as="h2" color="gray.200">
              Plant Food. Harvest Happiness.
            </Heading>
          </Box>
          <Box
            w={{ base: '100%', md: '50%' }}
            px={{ base: 0, md: 20 }}
            mx="auto"
            textAlign="center"
            color="gray.200"
            lineHeight="15px"
          >
            <Text fontSize={{ base: 'sm', md: 'base' }}>
              I think having land and not ruining it is the most beautiful art that anybody could
              ever want to own.
            </Text>
            <Text fontSize={{ base: 'sm', md: 'base' }}>- Andy Warhol</Text>
          </Box>
          <Box
            pos="relative"
            mx={{ base: 'auto', lg: 0 }}
            ml={{ lg: 'auto' }}
            rounded="md"
            py={{ base: 6 }}
            px={{ base: 6, md: 10 }}
            mt={{ base: 10, md: 16, lg: 24 }}
            w={{ base: 80, md: 115 }}
            bg="white"
            shadow={{ base: 2000, md: 'none' }}
          >
            <Box textAlign="center">
              <Heading as="h5">Log in to continue farming</Heading>
              <small>
                Don’t have a Complete Farmer account? <br className="md:hidden" />
                <Link as={ReachLink} color="cf.400" to="/register">
                  Sign up here
                </Link>
              </small>
            </Box>

            <Formik
              initialValues={{
                email: '',
                password: ''
              }}
              validationSchema={SignInSchema}
              onSubmit={(values, { setSubmitting }) => {
                (async () => {
                  try {
                    setSubmitting(true);
                    const response = await login(values);
                    setSubmitting(false);
                    if (response.code === 200) {
                      auth(response.data, rememberMe);
                      history.push('/home');
                    } else {
                      throw response.data.message;
                    }
                  } catch (error) {
                    let errMgs = error;
                    if (typeof error !== 'string') {
                      errMgs = 'Unexpected server error';
                    }
                    toast.error(errMgs, {
                      position: toast.POSITION.TOP_RIGHT
                    });
                  }
                })();
              }}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting
              }) => (
                <form className="block md:flex flex-wrap mt-10" onSubmit={handleSubmit}>
                  <FormInput
                    error={errors.email}
                    touched={touched.email}
                    className="w-full md:w-1/2 md:pr-2 mb-6 md:mb-0"
                    change={handleChange}
                    blur={handleBlur}
                    inputValue={values.email}
                    type="email"
                    id="email"
                    placeholder="johndoe@example.com"
                    title="Email"
                    inputType
                  />

                  <FormInput
                    error={errors.password}
                    touched={touched.password}
                    className="w-full md:w-1/2 md:pl-2"
                    change={handleChange}
                    blur={handleBlur}
                    inputValue={values.password}
                    type="password"
                    id="password"
                    placeholder="*********"
                    title="Password"
                    inputType
                    passwordShow
                  />

                  <FormCheckBox
                    name="rememberMe"
                    className="text-xs ml-auto mt-1"
                    click={() => setRememberMe(!rememberMe)}
                    content="Remember Me!"
                  />

                  <FormButton
                    loading={isSubmitting}
                    color="bg-cf-300"
                    name="Login"
                    className="w-full"
                    btnPad="px-4 py-4 my-3"
                  />

                  <small className="mx-auto">
                    Forgot password?{' '}
                    <Link as={ReachLink} to="/forgot-password">
                      <span className="text-cf-300">Click Here</span>
                    </Link>
                  </small>
                </form>
              )}
            </Formik>
          </Box>
        </Box>
      </Box>
      <AuthFooter />
    </Fragment>
  );
};

export default Login;
